import type { PackageJson } from '@ms-cloudpack/common-types';
import { readJson } from '@ms-cloudpack/json-utilities';
import { findProjectRoot } from '@ms-cloudpack/path-utilities';
import { table } from '@ms-cloudpack/task-reporter';
import path from 'path';
import semver from 'semver';
import { getPackageInfos, type PackageInfo } from 'workspace-tools';

interface NonSemverDep {
  parent: string | undefined;
  depName: string;
  type: string;
  version: string | undefined;
}

// resolutions might apply to prod or dev dependencies, so include it just in case
const prodDeps = ['dependencies', 'peerDependencies', 'resolutions'] as const;
const allDeps = [...prodDeps, 'devDependencies'] as const;

const depTypeNames = {
  dependencies: 'prod',
  peerDependencies: 'peer',
  devDependencies: 'dev',
  resolutions: 'resolution',
};

/**
 * Check the local packages in the monorepo for non-semver deps.
 * Returns either a table of those deps or a message saying there are none.
 */
export async function getNonSemverDeps(params: {
  cwd: string;
  /** Only include production dependencies */
  prod?: boolean;
  /** Use this directory as the root instead of calling `findProjectRoot` (for testing) */
  overrideProjectRoot?: string;
}): Promise<string> {
  const { cwd, prod, overrideProjectRoot } = params;

  const projectRoot = overrideProjectRoot || findProjectRoot(cwd) || cwd;
  const packages = getPackageInfos(projectRoot);

  // add the root package.json to the package infos
  const rootPkgJsonPath = path.join(projectRoot, 'package.json');
  const rootPkg = await readJson<PackageJson>(rootPkgJsonPath);
  if (!rootPkg) {
    throw new Error(`${rootPkgJsonPath} does not exist`);
  }
  if (!rootPkg.name || !rootPkg.version) {
    throw new Error(`${rootPkgJsonPath} is missing a name and/or version`);
  }
  packages[rootPkg.name] = rootPkg as PackageInfo;

  const depTypes = prod ? prodDeps : allDeps;

  const nonSemverDeps = Object.values(packages)
    .map((pkg: PackageJson) =>
      depTypes
        // get dependencies, peerDependencies, etc from the package
        .map((dt) => Object.entries(pkg[dt] || {}).map(([name, version]) => ({ name, version, type: dt })))
        .flat(1)
        // get non-semver versions
        .filter(({ version }) => !semver.validRange(version))
        .map(
          ({ name, version, type }): NonSemverDep => ({
            parent: pkg.name,
            depName: name,
            type: depTypeNames[type],
            version,
          }),
        ),
    )
    .flat();

  if (!nonSemverDeps.length) {
    return 'No non-semver dependencies found';
  }

  return (
    table(nonSemverDeps, {
      parent: 'Parent package',
      depName: 'Dependency',
      type: 'Dep type',
      version: 'Version',
    }) + '\n'
  );
}
